import { create } from 'zustand'

export type BotSkillLabel = 'Novice' | 'Easy' | 'Hard' | 'Expert' | 'Mix'

/** The advisor coaching level picked in the create-solo-game form — the same
 * three levels the in-game drawer offers ('beginner' = Advice & Feedback,
 * 'intermediate' = Feedback Only, 'off'). The bare default is 'beginner';
 * the create screen seeds the form from the player's SAVED advisor level on
 * focus (fresh players: Advice & Feedback; returning players: whatever they
 * last set in-game or in a previous create). */
export type SoloAdvisorLevel = 'beginner' | 'intermediate' | 'off'

export interface SoloGameState {
  gameName: string
  botCount: number
  botSkill: BotSkillLabel
  startingChips: number
  bigBlind: number
  turnDuration: number
  blindIncreases: boolean
  blindInterval: number
  advisorLevel: SoloAdvisorLevel
}

export interface GroupGameState {
  gameName: string
  startingChips: number
  bigBlind: number
  turnDuration: number
  blindIncreases: boolean
  blindInterval: number
  rebuy: boolean
  rebuyCount: number
  isPrivate: boolean
  advisor: boolean
  scheduleForLater: boolean
  scheduleDate: Date
}

interface CreateGameStore {
  solo: SoloGameState
  group: GroupGameState
  setSolo: (patch: Partial<SoloGameState>) => void
  setGroup: (patch: Partial<GroupGameState>) => void
  resetSolo: () => void
  resetGroup: () => void
  reset: () => void
}

const SOLO_DEFAULTS: SoloGameState = {
  gameName: '',
  botCount: 3,
  botSkill: 'Mix',
  startingChips: 10_000,
  bigBlind: 200,
  turnDuration: 30,
  blindIncreases: false,
  blindInterval: 5,
  advisorLevel: 'beginner'
}

const GROUP_DEFAULTS: GroupGameState = {
  gameName: '',
  startingChips: 10_000,
  bigBlind: 200,
  turnDuration: 30,
  blindIncreases: false,
  blindInterval: 5,
  rebuy: false,
  rebuyCount: 1,
  isPrivate: true,
  advisor: false,
  scheduleForLater: false,
  scheduleDate: new Date()
}

export const useCreateGameStore = create<CreateGameStore>((set) => ({
  solo: SOLO_DEFAULTS,
  group: GROUP_DEFAULTS,
  setSolo: (patch) => set((state) => ({ solo: { ...state.solo, ...patch } })),
  setGroup: (patch) => set((state) => ({ group: { ...state.group, ...patch } })),
  resetSolo: () => set({ solo: SOLO_DEFAULTS }),
  resetGroup: () => set({ group: GROUP_DEFAULTS }),
  reset: () => set({ solo: SOLO_DEFAULTS, group: GROUP_DEFAULTS })
}))
